import React from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { SEO } from '../components/SEO';
import { ChevronRightIcon } from 'lucide-react';
import { services } from '../data/services';
import { sectorPages } from '../data/sectorPages';
import { clientCases } from '../data/allClientCases';
import { articles, getAllClusters } from '../data/blogArticles';
import { blogPosts, getBlogCategories } from '../data/allBlogPosts';
import { buildBreadcrumbList, buildCollectionSchema } from '../lib/structuredData';
interface SitemapLink {
  label: string;
  path: string;
  external?: boolean;
}
const teamLinks: SitemapLink[] = [{
  label: 'Rocco',
  path: '/over-ons/rocco'
}, {
  label: 'Beer',
  path: '/over-ons/beer'
}, {
  label: 'Alex',
  path: '/over-ons/alex'
}, {
  label: 'Loïs Bruins',
  path: '/over-ons/lois'
}, {
  label: 'Jesse Put',
  path: '/over-ons/jesse'
}, {
  label: 'Norden',
  path: '/over-ons/norden'
}];
const bedrijfLinks: SitemapLink[] = [{
  label: 'Over ons',
  path: '/over-ons'
}, {
  label: 'Bibliotheek',
  path: '/bibliotheek'
}, {
  label: 'Prijsscan',
  path: '/prijsscan'
}, {
  label: 'Werken bij Noord Digital',
  path: '/sollicitatie'
}];
const juridischLinks: SitemapLink[] = [{
  label: 'Algemene voorwaarden',
  path: '/algemene-voorwaarden'
}, {
  label: 'Privacybeleid',
  path: '/privacy'
}, {
  label: 'Cookiebeleid',
  path: '/cookiebeleid'
}];
const LinkList: React.FC<{
  links: SitemapLink[];
}> = ({
  links
}) => <ul className="space-y-2.5" data-id="element-2318" data-mp="fwf3i5ffy5ta">
    {links.map(link => <li key={`${link.label}-${link.path}`} data-id="element-2319" data-mp="jyjwd1y2eyev">
        {link.external ? <a href={link.path} target="_blank" rel="noopener noreferrer" className="inline-flex items-start gap-2 text-[#4B5563] hover:text-[#004AC7] transition-colors duration-200" data-id="element-2320" data-mp="2hqee0okqoj4x">
            <ChevronRightIcon className="h-4 w-4 text-gray-300 mt-1 flex-shrink-0" aria-hidden="true" data-id="element-2321" data-mp="3p2dq3904b54r" />
            <span data-id="element-2322" data-mp="1xxk3zmg3lir">{link.label}</span>
          </a> : <Link to={link.path} className="inline-flex items-start gap-2 text-[#4B5563] hover:text-[#004AC7] transition-colors duration-200" data-id="element-2323" data-mp="1vbei5j47g36">
            <ChevronRightIcon className="h-4 w-4 text-gray-300 mt-1 flex-shrink-0" aria-hidden="true" data-id="element-2324" data-mp="bu0vzx73myiw" />
            <span data-id="element-2325" data-mp="llf06i9zwmid">{link.label}</span>
          </Link>}
      </li>)}
  </ul>;
export const SitemapPage: React.FC = () => {
  const serviceLinks: SitemapLink[] = services.map(service => ({
    label: service.title,
    path: service.link,
    external: service.external
  }));
  const sectorLinks: SitemapLink[] = sectorPages.map(page => ({
    label: page.sector,
    path: `/website-voor/${page.slug}`
  }));
  const caseLinks: SitemapLink[] = clientCases.map(clientCase => ({
    label: clientCase.clientName,
    path: `/cases/${clientCase.slug}`
  }));
  const clusters = getAllClusters();
  const blogCategories = getBlogCategories();
  const collectionItems = [...serviceLinks.filter(link => !link.external), ...sectorLinks, ...caseLinks, ...blogPosts.map(post => ({
    label: post.title,
    path: `/blog/${post.slug}`
  }))].map(link => ({
    name: link.label,
    path: link.path
  }));
  const sections: {
    title: string;
    intro: string;
    links: SitemapLink[];
  }[] = [{
    title: 'Diensten',
    intro: 'Wat we doen, van website tot methodiek.',
    links: serviceLinks
  }, {
    title: 'Website per branche',
    intro: 'Wat er per sector op een website thuishoort.',
    links: sectorLinks
  }, {
    title: 'Klantcases',
    intro: 'Werk dat we hebben opgeleverd, met de aanpak erbij.',
    links: caseLinks
  }, {
    title: 'Bedrijf',
    intro: 'Wie we zijn en wat je zelf kunt uitzoeken.',
    links: bedrijfLinks
  }, {
    title: 'Team',
    intro: 'De mensen met wie je direct schakelt.',
    links: teamLinks
  }, {
    title: 'Juridisch',
    intro: 'Voorwaarden en hoe we met gegevens omgaan.',
    links: juridischLinks
  }];
  return <div className="w-full min-h-screen bg-white text-[#111827] font-sans" data-id="element-2326" data-mp="3hx9frm0b6jv7">
      <SEO title="Alle pagina's op een rij, de sitemap | Noord Digital" description="Een overzicht van alle pagina's van Noord Digital: diensten, sectorpagina's, klantcases, kennisartikelen over SEO en de informatie over ons bedrijf." keywords="sitemap noord digital, overzicht pagina's, alle diensten noord digital" canonical="https://noorddigital.nl/sitemap" jsonLd={[buildCollectionSchema({
      name: "Sitemap van Noord Digital",
      description: "Overzicht van alle pagina's van Noord Digital, van diensten en sectorpagina's tot klantcases en kennisartikelen.",
      path: '/sitemap',
      items: collectionItems
    }), buildBreadcrumbList([{
      name: 'Sitemap',
      path: '/sitemap'
    }])]} data-id="element-2327" data-mp="3hudkk5fui9ag" />
      <Header data-id="element-2328" data-mp="2xwpzbr7mlc6k" />

      <main data-id="element-2329" data-mp="214cnx1qjzx5v">
        <section className="w-full bg-[#F7F8FA] pt-32 pb-14 md:pt-40 md:pb-16 border-b border-gray-200" data-id="element-2330" data-mp="214mfuuv5see7">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-2331" data-mp="262xnr01w8ets">
            <nav aria-label="Kruimelpad" className="mb-6" data-id="element-2332" data-mp="2a513i0vli5kv">
              <ol className="flex items-center gap-1.5 text-sm text-[#6B7280]" data-id="element-2333" data-mp="2a3hoedloe0ko">
                <li data-id="element-2334" data-mp="2f4x73z0druzw">
                  <Link to="/" className="hover:text-[#004AC7] transition-colors duration-200" data-id="element-2335" data-mp="2f5fbxk13zhjf">
                    Home
                  </Link>
                </li>
                <li aria-hidden="true" data-id="element-2336" data-mp="3ql3wy5usy6hl">
                  <ChevronRightIcon className="h-3.5 w-3.5 text-gray-300" data-id="element-2337" data-mp="1qg042v4i4r7c" />
                </li>
                <li className="font-medium text-[#111827]" aria-current="page" data-id="element-2338" data-mp="1mg7viif3yl1z">
                  Sitemap
                </li>
              </ol>
            </nav>
            <h1 className="font-bold text-[#111827] text-4xl md:text-5xl leading-tight mb-5 max-w-3xl" data-id="element-2339" data-mp="1vg4naswso019">
              Alle pagina's van Noord Digital op een rij
            </h1>
            <p className="text-[#4B5563] text-lg leading-relaxed max-w-2xl" data-id="element-2340" data-mp="17oh2msq4ll7f">
              Zoek je iets specifieks, dan vind je het hier sneller dan via het
              menu. Ook handig als je wilt zien voor welke branches we een eigen
              pagina hebben.
            </p>
          </div>
        </section>

        <section className="w-full bg-white py-16 md:py-20" data-id="element-2341" data-mp="1cntn56tiyg6u">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-2342" data-mp="oyhhui1bis9t">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-12" data-id="element-2343" data-mp="jzxvwhmq5ghg">
              {sections.map(section => <div key={section.title} data-id="element-2344" data-mp="2vm84aih0r3md">
                  <h2 className="font-bold text-[#111827] text-xl mb-2" data-id="element-2345" data-mp="30fkmoav3ue6o">
                    {section.title}
                  </h2>
                  <p className="text-sm text-[#6B7280] leading-relaxed mb-5" data-id="element-2346" data-mp="2mp7huhdifflk">
                    {section.intro}
                  </p>
                  <LinkList links={section.links} data-id="element-2347" data-mp="2qmvk65hj8anh" />
                </div>)}
            </div>
          </div>
        </section>

        <section className="w-full bg-[#F7F8FA] py-16 md:py-20 border-t border-gray-200" data-id="element-2348" data-mp="3je5bfw9ix7h5">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-2349" data-mp="35exbh15jdvbw">
            <h2 className="font-bold text-[#111827] text-2xl md:text-3xl mb-3" data-id="element-2350" data-mp="3acyw3qim22ph">
              Blogartikelen
            </h2>
            <p className="text-[#6B7280] leading-relaxed mb-10 max-w-2xl" data-id="element-2351" data-mp="1txxlic6nvsl1">
              {blogPosts.length} praktische stukken over vindbaarheid,
              websites, vormgeving en beheer, verdeeld over{' '}
              {blogCategories.length} onderwerpen.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-12" data-id="element-2352" data-mp="2lm0mz9ap8bw7">
              {blogCategories.map(category => <div key={category} data-id="element-2353" data-mp="2gnz0sfhz7wgi">
                  <h3 className="font-semibold text-[#111827] mb-4" data-id="element-2354" data-mp="2bochppfgvlzs">
                    {category}
                  </h3>
                  <LinkList links={blogPosts.filter(post => post.category === category).map(post => ({
                label: post.title,
                path: `/blog/${post.slug}`
              }))} data-id="element-2355" data-mp="39ce444rqwqc0" />
                </div>)}
            </div>
          </div>
        </section>

        <section className="w-full bg-white py-16 md:py-20" data-id="element-2356" data-mp="3s2vtq0ha75t5">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-2357" data-mp="3s4f8tnr7batc">
            <h2 className="font-bold text-[#111827] text-2xl md:text-3xl mb-3" data-id="element-2358" data-mp="3n9sxtlp17k9s">
              SEO-kennisbank
            </h2>
            <p className="text-[#6B7280] leading-relaxed mb-10 max-w-2xl" data-id="element-2359" data-mp="3gr04m03gdn4">
              {articles.length} artikelen, verdeeld over {clusters.length}{' '}
              onderwerpen. Uitleg zonder jargon, bedoeld om zelf betere keuzes te
              kunnen maken.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-12" data-id="element-2360" data-mp="dckdg7ln0b5y">
              {clusters.map(cluster => <div key={cluster} data-id="element-2361" data-mp="metnkwczu2it">
                  <h3 className="font-semibold text-[#111827] mb-4" data-id="element-2362" data-mp="mdsayngbx7yz">
                    {cluster}
                  </h3>
                  <LinkList links={articles.filter(article => article.cluster === cluster).map(article => ({
                label: article.title,
                path: `/seo/blog/${article.slug}`
              }))} data-id="element-2363" data-mp="r8o4js17s3e7" />
                </div>)}
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-2364" data-mp="1o03vexvy7f9m" />
    </div>;
};